import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /** @type {number | undefined} */
  #limitMessageTimeout;

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#limitMessageTimeout) {
      clearTimeout(this.#limitMessageTimeout);
      this.#limitMessageTimeout = undefined;
    }
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const previousValue = Number(quantityInput.value);

    /* Theme change owner: Novicell — when a line already meets/exceeds its max (e.g. store stock
       dropped after it was added), keep the current value instead of stepping up or snapping down;
       why: let the shopper reduce it themselves while seeing the limit message. */
    const maxValue = quantityInput.max === '' ? null : Number(quantityInput.max);
    const atOrOverMax = maxValue !== null && Number.isFinite(maxValue) && previousValue >= maxValue;

    if (!atOrOverMax) {
      quantityInput.stepUp();
    }

    this.#onQuantityChange({ previousValue, direction: 'increase' });
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange({ direction: 'decrease' });
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange({ direction: 'manual' });
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange({ previousValue, direction } = {}) {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules(direction);
    this.#handleLimitMessage({ previousValue, direction });
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine), quantityInput.dataset.cartKey));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = (direction) => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;
    const hasMin = min !== '' && min !== undefined;
    const hasMax = max !== '' && max !== undefined;
    const minValue = hasMin ? Number(min) : null;
    const maxValue = hasMax ? Number(max) : null;
    const currentValue = Number(newValue);

    if (hasMin && minValue !== null && !Number.isNaN(minValue) && Number.isFinite(minValue) && currentValue < minValue) {
      quantityInput.value = String(minValue);
    }

    /* Theme change owner: Novicell — only clamp DOWN to max on manual entry. The +/- buttons handle
       the ceiling themselves (native stepUp respects max; increaseQuantity blocks over-max), so a
       decreasing or already-over-stock value is never snapped straight to the max. */
    if (direction === 'increase' || direction === 'decrease') return;

    if (hasMax && maxValue !== null && !Number.isNaN(maxValue) && Number.isFinite(maxValue) && currentValue > maxValue) {
      quantityInput.value = String(maxValue);
    }
  };

  #handleLimitMessage({ previousValue, direction } = {}) {
    const message = this.#getLimitMessageElement();
    if (!message) return;

    if (direction !== 'increase') {
      this.#hideLimitMessage(message);
      return;
    }

    const { quantityInput } = this.refs;
    const maxAttr = quantityInput.max;
    if (maxAttr === '' || maxAttr === undefined) {
      this.#hideLimitMessage(message);
      return;
    }

    const maxValue = Number(maxAttr);
    if (!Number.isFinite(maxValue)) {
      this.#hideLimitMessage(message);
      return;
    }

    const currentValue = Number(quantityInput.value);
    const attemptedIncrease = Number.isFinite(previousValue) && currentValue === previousValue && currentValue >= maxValue;

    if (attemptedIncrease) {
      this.#showLimitMessage(message);
    } else {
      this.#hideLimitMessage(message);
    }
  }

  #getLimitMessageElement() {
    /* Theme change owner: Novicell — also support a limit message rendered inside the quantity
       selector itself; why: the cart has no product-quantity-updater wrapper, so US lines that hit
       their store-stock max got no feedback. PDP keeps using its wrapper via the fallback below. */
    const scopedMessage = this.querySelector('[data-quantity-limit-message]');
    if (scopedMessage instanceof HTMLElement) return scopedMessage;

    const container = this.closest('product-quantity-updater');
    const message = container?.querySelector('[data-quantity-limit-message]');
    return message instanceof HTMLElement ? message : null;
  }

  #showLimitMessage(message) {
    message.hidden = false;
    if (this.#limitMessageTimeout) clearTimeout(this.#limitMessageTimeout);
    this.#limitMessageTimeout = setTimeout(() => {
      message.hidden = true;
    }, 3000);
  }

  #hideLimitMessage(message) {
    message.hidden = true;
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
